import { format, parseISO } from "date-fns";
import { FileText, Send, Wallet } from "lucide-react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDateLocale } from "../../personal/useDateLocale";
import type { FinanceCharge, FinanceChargeStatus } from "../../types";
import { useFormatMoney } from "../useFormatMoney";
import type { ChargeActions } from "./ChargeList";
import { ChargeStatusBadge } from "./ChargeStatusBadge";

export interface ChargeRow {
  charge: FinanceCharge;
  status: FinanceChargeStatus;
  companyName: string;
}

interface ChargeRowsProps {
  rows: ChargeRow[];
  actions: ChargeActions;
  isMobile: boolean;
}

const RowActions = ({
  row: { charge, status },
  actions,
}: {
  row: ChargeRow;
  actions: ChargeActions;
}) => {
  const translate = useTranslate();
  const canPay = status !== "paid" && status !== "cancelled";
  return (
    <div
      className="flex justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      {status === "pending" && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => actions.onMarkSent(charge)}
          aria-label={translate("crm.finance.charges.mark_sent")}
          title={translate("crm.finance.charges.mark_sent")}
        >
          <Send />
        </Button>
      )}
      {canPay && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => actions.onRegisterPayment(charge)}
          aria-label={translate("crm.finance.charges.register_payment")}
        >
          <Wallet />
          <span className="hidden lg:inline">
            {translate("crm.finance.charges.register_payment")}
          </span>
        </Button>
      )}
    </div>
  );
};

const hasInvoice = (charge: FinanceCharge) =>
  Boolean(charge.invoice_number) || (charge.invoice_files ?? []).length > 0;

const InvoiceCell = ({ charge }: { charge: FinanceCharge }) => {
  const translate = useTranslate();
  const files = charge.invoice_files ?? [];
  if (!hasInvoice(charge)) {
    return <span className="text-xs text-muted-foreground">–</span>;
  }
  return (
    <span
      className="flex items-center gap-1 text-xs"
      title={translate("crm.finance.charges.fields.invoice_number")}
    >
      <FileText className="size-3" />
      {charge.invoice_number ?? files.length}
    </span>
  );
};

type ChargeViewProps = Omit<ChargeRowsProps, "isMobile">;

const useChargeDates = () => {
  const { dateFnsLocale } = useDateLocale();
  return {
    day: (value: string) =>
      format(parseISO(value), "P", { locale: dateFnsLocale }),
    month: (value: string) =>
      format(parseISO(value), "MMM yyyy", { locale: dateFnsLocale }),
  };
};

const ChargeCards = ({ rows, actions }: ChargeViewProps) => {
  const formatMoney = useFormatMoney();
  const { day } = useChargeDates();
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li
          key={row.charge.id}
          className="flex flex-col gap-2 rounded-lg border p-3"
          onClick={() => actions.onOpen(row.charge)}
          data-testid="charge-row"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate font-medium">{row.companyName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {row.charge.description}
              </p>
            </div>
            <p className="font-semibold whitespace-nowrap">
              {formatMoney(row.charge.amount)}
            </p>
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ChargeStatusBadge status={row.status} />
              {day(row.charge.due_date)}
              {hasInvoice(row.charge) && <InvoiceCell charge={row.charge} />}
            </div>
            <RowActions row={row} actions={actions} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const ChargeTable = ({ rows, actions }: ChargeViewProps) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const { day, month } = useChargeDates();
  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{translate("crm.finance.fields.company")}</TableHead>
            <TableHead>{translate("crm.finance.fields.description")}</TableHead>
            <TableHead>
              {translate("crm.finance.charges.fields.kind")}
            </TableHead>
            <TableHead>
              {translate("crm.finance.fields.competence_month")}
            </TableHead>
            <TableHead>{translate("crm.finance.fields.due_date")}</TableHead>
            <TableHead className="text-right">
              {translate("crm.finance.fields.amount")}
            </TableHead>
            <TableHead>{translate("crm.finance.fields.status")}</TableHead>
            <TableHead>
              {translate("crm.finance.charges.fields.invoice")}
            </TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.charge.id}
              className="cursor-pointer"
              onClick={() => actions.onOpen(row.charge)}
              data-testid="charge-row"
            >
              <TableCell className="max-w-48 truncate font-medium">
                {row.companyName}
              </TableCell>
              <TableCell className="max-w-56 truncate text-muted-foreground">
                {row.charge.description}
              </TableCell>
              <TableCell className="text-xs">
                {translate(`crm.finance.charges.kinds.${row.charge.kind}`)}
              </TableCell>
              <TableCell className="text-xs first-letter:uppercase">
                {month(row.charge.competence_month)}
              </TableCell>
              <TableCell className="text-xs">
                {day(row.charge.due_date)}
              </TableCell>
              <TableCell className="text-right font-medium whitespace-nowrap">
                {formatMoney(row.charge.amount)}
              </TableCell>
              <TableCell>
                <ChargeStatusBadge status={row.status} />
              </TableCell>
              <TableCell>
                <InvoiceCell charge={row.charge} />
              </TableCell>
              <TableCell>
                <RowActions row={row} actions={actions} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/** Receivables as a table on desktop and as cards on mobile */
export const ChargeRows = ({ rows, actions, isMobile }: ChargeRowsProps) =>
  isMobile ? (
    <ChargeCards rows={rows} actions={actions} />
  ) : (
    <ChargeTable rows={rows} actions={actions} />
  );
